/**
 * Analytics · Data Source Health
 * Demo content - see src/demo/analytics.ts.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AppShell } from '../../components/shell/AppShell';
import { Card, Kpi, PageHead, Table, Tag, Bar, Note, DemoBadge } from '../../components/ui';
import { ChartFrame, Trend, Bars, STATUS } from '../../components/charts';
import { HEALTH } from '../../demo/analytics';

export const DataSourceHealthPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <AppShell
      title="Data Source Health"
      sub="Analytics"
      actions={
        <>
          <DemoBadge />
          <button className="btn btn-g" onClick={() => navigate('/settings/datasources')}>
            Manage sources
          </button>
        </>
      }
    >
      <PageHead
        eyebrow="Analytics"
        title="Data source health"
        lede="An answer is only as fresh as the source behind it. This is what each connector is doing: whether it is syncing, how fast it responds, and what it has failed to read."
      />

      <div className="g g4" style={{ marginBottom: 12 }}>
        {HEALTH.kpis.map((k) => (
          <Kpi
            key={k.label}
            label={k.label}
            value={k.value}
            unit={'unit' in k ? (k as any).unit : undefined}
            color={k.color}
            icon={k.icon as any}
            pip={'pip' in k ? (k as any).pip : undefined}
            dir={('dir' in k ? (k as any).dir : '') as any}
            sub={'sub' in k ? (k as any).sub : undefined}
          />
        ))}
      </div>

      <div className="g g2" style={{ marginBottom: 12 }}>
        <ChartFrame title="Query latency by source" sub="median ms, last 7 days">
          <Trend
            data={HEALTH.latency}
            x="day"
            series={[
              { key: 'salesforce', label: 'Salesforce' },
              { key: 'sap', label: 'SAP' },
              { key: 'workday', label: 'Workday' },
            ]}
          />
        </ChartFrame>

        <ChartFrame title="Sync outcomes" sub="runs per day, last 7 days">
          <Bars
            data={HEALTH.syncOutcomes}
            x="day"
            stacked
            series={[
              { key: 'success', label: 'Succeeded' },
              { key: 'failed', label: 'Failed' },
            ]}
          />
        </ChartFrame>
      </div>

      <Card title="Every connector" sub="uptime and sync success over the last 7 days" flush>
        <Table
          cols={[
            { t: 'Source' },
            { t: 'State' },
            { t: 'Uptime', num: true },
            { t: 'Sync success' },
            { t: 'Latency', num: true },
            { t: 'Errors', num: true },
            { t: 'Last sync' },
          ]}
          rows={HEALTH.sources.map((s) => [
            <b>{s.name}</b>,
            <Tag tone={s.tone as any} dot>
              {s.state}
            </Tag>,
            s.uptime ? `${s.uptime}%` : '—',
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Bar
                pct={s.success}
                width={60}
                color={s.success >= 95 ? STATUS.good : s.success > 0 ? STATUS.warning : STATUS.serious}
              />
              <span className="mono">{s.success ? `${s.success}%` : '—'}</span>
            </span>,
            s.latency ? `${s.latency}ms` : '—',
            s.errors ? <Tag tone="r">{s.errors}</Tag> : '0',
            <span className="dim">{s.last}</span>,
          ])}
        />
      </Card>

      <Note style={{ marginTop: 12 }}>
        <b>Odoo Egypt has been failing for 3 hours.</b> XML-RPC authentication is being rejected, so
        anything it would answer — Egypt accounting, inventory and manufacturing — is missing from
        today's answers rather than silently stale.
      </Note>
    </AppShell>
  );
};
